import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useCallback, useEffect, useState } from "react";
import { Grid } from "@material-ui/core";
import download from "../../../assets/images/adminSection/download-02.svg";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SaveIcon from "@mui/icons-material/Save";
import CustomInput from "../../../components/common/inputField";
import dashboardService from "../../../services/DashboardServices";
import { ApprovedIdeasDashboard } from "../../../types/adminSection";
import CustomSelect from "../../../components/common/customSelect";
import EditIcon from "../../../assets/images/editIcon.svg";
import { IdeaStatusSPOCEnum } from "../../../helper/typeEnum";
import { showToast } from "../../../components/common/toastNotification";
import IdeaCapturingModalPopUp from "../../successPopUp/ideaCapturePopUp";
import { useNavigate } from "react-router-dom";
import { REImagingUpdateData } from "../../../types/dashboard";
import toast from "react-hot-toast";

const ApprovedIdeasDashboardSPOC: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [tableData, setTableData] = useState<ApprovedIdeasDashboard[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRewardAcceptanceFormOpen, setIsRewardAcceptanceFormOpen] =
    useState(false);
  const [rewardAcceptanceFormData, setRewardAcceptanceFormData] =
    useState<REImagingUpdateData | null>(null);
  const [filters, setFilters] = useState<{
    year: number;
    month: number;
    ideaStatus: number | null;
  }>({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    ideaStatus: null as number | null,
  });

  const [editFormData, setEditFormData] = useState<{
    ideaStatus: string;
    spocRemarks: string;
  }>({ ideaStatus: "", spocRemarks: "" });
  const [editRowId, setEditRowId] = useState<number | null>(null);

  const handleEditClick = (row: ApprovedIdeasDashboard) => {
    setEditRowId(row.id ?? null);
    setEditFormData({
      ideaStatus: row.ideaStatus?.toString() ?? "",
      spocRemarks: row.spocRemarks ?? "",
    });
  };

  const fetchTableData = useCallback(async () => {
    const input = {
      pageNo: page + 1,
      pageSize,
      month: filters.month,
      year: filters.year,
      ideaStatus: filters.ideaStatus,
    };

    try {
      const response = await dashboardService.getApprovedIdeasTableDataSPOC(
        input
      );

      const records = response.data?.data?.data || [];
      const total = response.data?.data?.totalRecords || 0;

      setTableData(records);
      setTotalPageCount(total);
    } catch (error) {
      console.error("Failed to fetch table data", error);
      setTableData([]);
      setTotalPageCount(0);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    fetchTableData();
  }, [page, pageSize, filters, fetchTableData]);
  // Utility for year options
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear - 2; y <= currentYear + 2; y++) {
      years.push({ value: y, label: y.toString() });
    }
    return years;
  };

  const yearOptions = generateYearOptions();
  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];
  const handleExportExcel = async () => {
    setIsDownloading(true);
    const exportPayload: ApprovedIdeasDashboard = {
      pageNo: page + 1,
      pageSize,
      month: filters.month,
      year: filters.year,
      ideaStatus: filters.ideaStatus,
    };

    try {
      const response = await dashboardService.exportApprovedIdeasTableDataSPOC(
        exportPayload
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileURL = window.URL.createObjectURL(blob);
      const fileLink = document.createElement("a");

      fileLink.href = fileURL;
      fileLink.setAttribute("download", "Implementation_PendingList.xlsx");

      document.body.appendChild(fileLink);
      fileLink.click();
      document.body.removeChild(fileLink);
      toast.success("Excel file exported successfully.");
    } catch (error) {
      console.error("Excel export failed.", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRowDataSave = async (row: ApprovedIdeasDashboard) => {
    if (
      !editFormData.ideaStatus ||
      editFormData.ideaStatus.trim() === "" ||
      editFormData.ideaStatus === "0"
    ) {
      showToast("Idea Status is required", "error");
      return;
    }

    if (!editFormData.spocRemarks || editFormData.spocRemarks.trim() === "") {
      showToast("Remarks are required", "error");
      return;
    }

    try {
      const payload = {
        id: row.id ?? 0,
        ideaStatus: Number(editFormData.ideaStatus),
        spocRemarks: editFormData.spocRemarks,
      };

      const response = await dashboardService.updateApprovedIdeasTableDataSPOC(
        payload
      );

      if (response.data?.success) {
        showToast(response.data.message, "success");
        fetchTableData();
        setEditRowId(null);
      } else {
        showToast(response.data.message, "error");
      }
    } catch (error) {
      console.error("Error updating idea:", error);
    }
  };

  const ideaStatusOptions = Object.keys(IdeaStatusSPOCEnum)
    .filter((key) => isNaN(Number(key)))
    .map((label) => ({
      value: IdeaStatusSPOCEnum[label as keyof typeof IdeaStatusSPOCEnum],
      label,
    }));
  const fetchRewardAndRecognitionStatus = async (id: number) => {
    setIsRewardAcceptanceFormOpen(true);

    try {
      const response =
        await dashboardService.GetREImagineAceecsApproveRejectAPI(id);
      const finalResponse = response.data;
      if (finalResponse.success) {
        setRewardAcceptanceFormData(finalResponse.data);
      }
    } catch (error) {
      console.error("Error fetching reward status:", error);
    }
  };

  return (
    <>
      <Box p={3}>
        <Card
          sx={{
            backgroundColor: "#212121",
            mb: 2,
            borderRadius: 0,
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Grid
              container
              style={{
                marginBottom: 8,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
                Approved Ideas Dashboard
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", sm: "space-between" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 2, sm: 0 },
                }}
              >
                <Box
                  sx={{
                    minWidth: { xs: 140, sm: 140 },
                    marginLeft: { xs: 0, sm: 2 },
                  }}
                >
                  {" "}
                  <CustomSelect
                    name="year"
                    value={filters.year}
                    onChange={(event) => {
                      setFilters((prev) => ({
                        ...prev,
                        year: Number(event.target.value),
                      }));
                    }}
                    placeHolder="Select Year"
                    options={yearOptions}
                    backgroundColor="#212121"
                  />
                </Box>

                <Box
                  sx={{
                    minWidth: { xs: 140, sm: 140 },
                    marginLeft: { xs: 0, sm: 2 },
                  }}
                >
                  {" "}
                  <CustomSelect
                    name="month"
                    value={filters.month}
                    onChange={(event) => {
                      setFilters((prev) => ({
                        ...prev,
                        month: Number(event.target.value),
                      }));
                    }}
                    placeHolder="Select Month"
                    options={monthOptions}
                    backgroundColor="#212121"
                  />
                </Box>
                <Box
                  sx={{
                    minWidth: { xs: 140, sm: 140 },
                    marginLeft: { xs: 0, sm: 2 },
                  }}
                >
                  <CustomSelect
                    name="status"
                    value={filters.ideaStatus ?? ""}
                    onChange={(event) => {
                      const val = event.target.value;
                      setFilters((prev) => ({
                        ...prev,
                        ideaStatus: val === "" ? null : Number(val),
                      }));
                    }}
                    placeHolder="Select Status"
                    resettable
                    options={ideaStatusOptions}
                    backgroundColor="#212121"
                  />
                </Box>
                <Button
                  className=" downloadBtn f-14"
                  variant="contained"
                  sx={{
                    minWidth: { xs: 140, sm: 140 },
                    display: "flex",
                    justifyContent: "space-between",
                    marginLeft: { xs: 0, sm: 2 },
                  }}
                  onClick={handleExportExcel}
                  disabled={isDownloading}
                >
                  <Typography>Download</Typography>
                  <Box>
                    {isDownloading ? (
                      <CircularProgress
                        size={20}
                        sx={{ color: "#fff", marginTop: 1 }}
                      />
                    ) : (
                      <img
                        src={download}
                        width={20}
                        style={{ marginTop: 7 }}
                        alt="download"
                      />
                    )}
                  </Box>
                </Button>
              </Box>
            </Grid>

            <TableContainer
              sx={{
                backgroundColor: "#212121",
                padding: 0,
                minHeight: "300px",
              }}
            >
              <Table sx={{ overflow: "scroll" }}>
                <TableHead>
                  <TableRow>
                    {[
                      "Unique Idea No",
                      "Requested by",
                      "Area of impacts",
                      "Requester email ID",
                      "Implementation Status",
                      "Remarks",
                      "Action",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          color: "#fff",
                          background: "#0A0A0A",
                          fontWeight: "600",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={12}
                        sx={{
                          color: "#fff",
                          textAlign: "center",
                          alignItems: "center",
                        }}
                      >
                        No data available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ color: "#fff", minWidth: 100 }}>
                          {row.id}
                        </TableCell>
                        <TableCell sx={{ color: "#fff", minWidth: 60 }}>
                          {row.name}
                        </TableCell>
                        <TableCell sx={{ color: "#fff", minWidth: 100 }}>
                          {row.areasOfImpact}
                        </TableCell>
                        <TableCell sx={{ color: "#fff", minWidth: 100 }}>
                          {row.email}
                        </TableCell>
                        <TableCell sx={{ color: "#fff", minWidth: 150 }}>
                          {editRowId === row.id ? (
                            <CustomSelect
                              name="ideaStatus"
                              value={
                                editFormData.ideaStatus &&
                                editFormData.ideaStatus !== "0"
                                  ? editFormData.ideaStatus
                                  : ""
                              }
                              backgroundColor="transparent"
                              onChange={(event) =>
                                setEditFormData((prev) => ({
                                  ...prev,
                                  ideaStatus: event.target.value.toString(),
                                }))
                              }
                              placeHolder="Select Status"
                              options={ideaStatusOptions}
                            />
                          ) : (
                            ideaStatusOptions.find(
                              (opt) => opt.value === row.ideaStatus
                            )?.label ?? "-"
                          )}
                        </TableCell>

                        <TableCell sx={{ color: "#fff", minWidth: 150 }}>
                          {editRowId === row.id ? (
                            <CustomInput
                              label=""
                              type="text"
                              marginBottom="8px"
                              name="spocRemarks"
                              onChange={handleInputChange}
                              placeholder="Enter remarks"
                              value={editFormData.spocRemarks}
                              backgroundColor="transparent"
                            />
                          ) : (
                            <Box>{row.spocRemarks?.trim() || "-"}</Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: "#fff", minWidth: 80 }}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              {editRowId === row.id ? (
                                <SaveIcon
                                  style={{
                                    cursor: "pointer",

                                    width: 20,
                                    height: 20,
                                  }}
                                  onClick={() => handleRowDataSave(row)}
                                />
                              ) : (
                                <img
                                  src={EditIcon}
                                  alt="Edit"
                                  style={{ cursor: "pointer" }}
                                  width={20}
                                  height={20}
                                  onClick={() => handleEditClick(row)}
                                />
                              )}
                            </Box>
                            <IconButton
                              onClick={() =>
                                fetchRewardAndRecognitionStatus(row.id ?? 0)
                              }
                            >
                              <VisibilityIcon sx={{ color: "#fff" }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Grid
              style={{
                justifyContent: "flex-end",
                alignItems: "center",
                display: "flex",
                marginTop: 16,
                padding: 8,
              }}
            >
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={totalPageCount}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  color: "#ffffff",
                  "& .MuiSelect-icon": { color: "#ffffff" },
                  "& .MuiSvgIcon-root": { color: "#ffffff" },
                }}
              />
            </Grid>
          </CardContent>
        </Card>
      </Box>
      {isRewardAcceptanceFormOpen && rewardAcceptanceFormData && (
        <IdeaCapturingModalPopUp
          open={isRewardAcceptanceFormOpen}
          onClose={() => {
            setIsRewardAcceptanceFormOpen(false);
            navigate("/implementation-pendingView");
            fetchTableData();
          }}
          reImagineUpdateData={rewardAcceptanceFormData}
        />
      )}
    </>
  );
};

export default ApprovedIdeasDashboardSPOC;
